import React from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  List,
  ListItem,
  Avatar,
  Divider,
  useColorModeValue,
  Tag,
  VStack,
} from "@chakra-ui/react";
import { FaGraduationCap } from "react-icons/fa";

// --- Card Component Base Style (No changes needed here for responsiveness) ---
const CardContainer = (props) => {
  const bg = useColorModeValue("white", "gray.700");
  const shadow = useColorModeValue("xl", "dark-lg");

  return (
    <Box
      bg={bg}
      p={{ base: 4, md: 7 }} // Adjusted padding for mobile (base)
      borderRadius="xs"
      boxShadow="md"
      borderWidth="1px"
      borderColor={useColorModeValue("gray.100", "gray.600")}
      w="100%"
      transition="all 0.3s ease"
      {...props}
    />
  );
};
// --------------------------------------------------------

function StudentProgressCard({ studentData }) {
  const hoverBg = useColorModeValue("blue.50", "gray.700");
  const headingColor = useColorModeValue("gray.900", "white");
  const dividerColor = useColorModeValue("gray.100", "gray.700");
  const topRankBg = useColorModeValue("teal.50", "teal.900");
  const topRankShadow = useColorModeValue("md", "dark-lg");

  const getMarkColor = (mark) => {
    if (mark >= 90) return "teal.500";
    if (mark >= 75) return "blue.500";
    return "orange.500";
  };

  const sortedStudents = studentData
    .sort((a, b) => b.highest_marks - a.highest_marks)
    .slice(0, 5);

  return (
    <CardContainer>
      <Flex align="center" mb={1}>
        <Box as={FaGraduationCap} size="24px" color="teal.500" mr={2} />

        <Heading size={{ base: "sm", md: "md" }} color={headingColor}>
          {/* Adjusted heading size */} Top Student Progress
        </Heading>
      </Flex>

      <Text
        fontSize={{ base: "xs", md: "sm" }} // Adjusted description size
        color="gray.500"
        mb={6}
        borderBottom="1px"
        borderColor={dividerColor}
        pb={3}
      >
        Highest scoring students in your assigned subjects.
      </Text>

      <List spacing={0}>
        {sortedStudents.length > 0 ? (
          sortedStudents.map((student, index) => {
            const markColor = getMarkColor(student.highest_marks);
            const isTopStudent = index === 0;

            return (
              <VStack key={`${student.student_name}-${index}`} align="stretch" spacing={0}>
                <ListItem
                  display="flex"
                  alignItems="center"
                  p={3} // Reduced vertical padding on mobile
                  borderRadius="xl"
                  bg={isTopStudent ? topRankBg : "transparent"}
                  boxShadow={isTopStudent ? topRankShadow : "none"}
                  transition="all 0.2s ease-in-out"
                  _hover={{ bg: hoverBg, transform: "translateY(-1px)" }}
                  cursor="pointer"
                  my={1}
                >
                  {/* Rank Tag */}
                  <Tag
                    size={{ base: "sm", md: "lg" }} // Smaller rank tag on mobile
                    mr={{ base: 3, md: 4 }}
                    colorScheme={isTopStudent ? "yellow" : "gray"}
                    variant={isTopStudent ? "solid" : "subtle"}
                    fontWeight="extrabold"
                    minW={{ base: "35px", md: "45px" }}
                    justifyContent="center"
                  >
                    #{index + 1}
                  </Tag>
                  {/* Avatar */}

                  <Avatar
                    size={{ base: "sm", md: "md" }} // Smaller avatar on mobile
                    name={student.student_name}
                    src={student.image_url}
                    mr={{ base: 3, md: 4 }}
                    border="2px solid"
                    borderColor={isTopStudent ? "teal.400" : "gray.300"}
                  />
                  {/* Content and Score */}
                  <Flex
                    key={student.id}
                    justify="space-between"
                    w="full"
                    align={{ base: "flex-start", md: "center" }} // Align text to the top on mobile
                    direction={{ base: "column", sm: "row" }} // Stack on smallest screens if needed
                  >
                    <Box>
                      <Text
                        fontSize={{ base: "md", md: "lg" }} // Name size adjustment
                        fontWeight="bold"
                        color={headingColor}
                      >
                        {student.student_name}
                      </Text>

                      <Text
                        fontSize={{ base: "xs", md: "sm" }}
                        color="gray.500"
                      >
                        Class:
                        <Text as="span" fontWeight="semibold">
                          {student.class}
                        </Text>
                      </Text>
                    </Box>

                    <Text
                      fontSize={{
                        base: "2xl",
                        md: isTopStudent ? "4xl" : "3xl",
                      }} // Crucial: Smaller size on mobile
                      fontWeight="black"
                      color={markColor}
                      letterSpacing="tight"
                      mt={{ base: 1, sm: 0 }} // Add a slight margin on mobile if stacked
                    >
                      {student.highest_marks}%
                    </Text>
                  </Flex>
                </ListItem>
              </VStack>
            );
          })
        ) : (
          <Text textAlign="center" color="gray.500" p={4}>
            No subject results available or students assigned.
          </Text>
        )}
      </List>
    </CardContainer>
  );
}

export default StudentProgressCard;
